// AI Settings Component for B2B Dashboard
// Allows agencies to customize AI assistant behavior

import { useState, useEffect } from 'react';

const PERSONALITIES = [
  { id: 'professional', emoji: '🎩', label: 'Professional', desc: 'Formal, business-like' },
  { id: 'friendly', emoji: '😊', label: 'Friendly', desc: 'Warm, conversational' },
  { id: 'luxury', emoji: '✨', label: 'Luxury', desc: 'Sophisticated, premium' },
  { id: 'enthusiastic', emoji: '🎉', label: 'Enthusiastic', desc: 'Energetic, excited' },
  { id: 'calm', emoji: '🧘', label: 'Calm', desc: 'Relaxed, no-pressure' }
];

const EXPERTISE_OPTIONS = [
  { id: 'budget', label: 'Budget-conscious options', emoji: '💰' },
  { id: 'luxury', label: 'Luxury experiences', emoji: '💎' },
  { id: 'family', label: 'Family-friendly trips', emoji: '👨‍👩‍👧‍👦' },
  { id: 'adventure', label: 'Adventure activities', emoji: '🏔️' },
  { id: 'allinclusive', label: 'All-inclusive packages', emoji: '🏨' },
  { id: 'cruise', label: 'Cruise expertise', emoji: '🚢' },
  { id: 'honeymoon', label: 'Honeymoons & romance', emoji: '💕' },
  { id: 'group', label: 'Group travel', emoji: '👥' }
];

const DEFAULT_AI_SETTINGS = {
  personality: 'friendly',
  assistant_name: '',
  response_length: 'balanced',
  emoji_usage: 'minimal',
  lead_capture_level: 'balanced',
  custom_instructions: '',
  expertise_focus: [],
  faq_responses: []
};

export default function AISettingsSection({ agency, token, onSaveSuccess }) {
  const [aiSettings, setAiSettings] = useState(DEFAULT_AI_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [newFaq, setNewFaq] = useState({ trigger: '', response: '' });

  // Initialize from agency data
  useEffect(() => {
    if (agency?.ai_settings) {
      setAiSettings({
        ...DEFAULT_AI_SETTINGS,
        ...agency.ai_settings
      });
    }
  }, [agency]);

  const handleChange = (field, value) => {
    setAiSettings(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setError(null);
  };

  const toggleExpertise = (id) => {
    setAiSettings(prev => {
      const current = prev.expertise_focus || [];
      const updated = current.includes(id)
        ? current.filter(e => e !== id)
        : [...current, id];
      return { ...prev, expertise_focus: updated };
    });
    setSaveSuccess(false);
  };

  const addFaq = () => {
    if (!newFaq.trigger.trim() || !newFaq.response.trim()) return;
    
    setAiSettings(prev => ({
      ...prev,
      faq_responses: [...(prev.faq_responses || []), { ...newFaq }]
    }));
    setNewFaq({ trigger: '', response: '' });
    setSaveSuccess(false);
  };

  const removeFaq = (index) => {
    setAiSettings(prev => ({
      ...prev,
      faq_responses: prev.faq_responses.filter((_, i) => i !== index)
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch('/api/b2b/agency/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ai_settings: aiSettings })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }

      setSaveSuccess(true);
      if (onSaveSuccess) onSaveSuccess();
      
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="b2b-settings-section">
      <h2 className="b2b-settings-section-title">🤖 AI Assistant Settings</h2>
      <p className="b2b-settings-section-desc">
        Customize how your AI assistant interacts with visitors
      </p>

      {/* Personality Selection */}
      <div style={{ marginBottom: '32px' }}>
        <div className="b2b-settings-label" style={{ marginBottom: '12px' }}>Personality</div>
        <div className="b2b-settings-label-desc" style={{ marginBottom: '16px' }}>
          Choose how your AI assistant communicates
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {PERSONALITIES.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleChange('personality', p.id)}
              style={{
                padding: '16px 20px',
                borderRadius: '12px',
                border: aiSettings.personality === p.id 
                  ? '2px solid #d4a853' 
                  : '2px solid #e5e7eb',
                background: aiSettings.personality === p.id 
                  ? '#fef9e7' 
                  : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: '120px',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{p.emoji}</div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>{p.label}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Assistant Name */}
      <div className="b2b-settings-row">
        <div>
          <div className="b2b-settings-label">Assistant Name</div>
          <div className="b2b-settings-label-desc">Give your AI a friendly name (optional)</div>
        </div>
        <input
          type="text"
          className="b2b-form-input"
          placeholder="e.g., Luna, Alex, Travel Buddy"
          style={{ maxWidth: '250px' }}
          value={aiSettings.assistant_name || ''}
          onChange={(e) => handleChange('assistant_name', e.target.value)}
        />
      </div>

      {/* Response Length */}
      <div className="b2b-settings-row">
        <div>
          <div className="b2b-settings-label">Response Length</div>
          <div className="b2b-settings-label-desc">How detailed should responses be?</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['concise', 'balanced', 'detailed'].map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => handleChange('response_length', opt)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: aiSettings.response_length === opt 
                  ? '2px solid #d4a853' 
                  : '1px solid #e5e7eb',
                background: aiSettings.response_length === opt ? '#fef9e7' : 'white',
                cursor: 'pointer',
                fontWeight: aiSettings.response_length === opt ? 600 : 400,
                textTransform: 'capitalize'
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Emoji Usage */}
      <div className="b2b-settings-row">
        <div>
          <div className="b2b-settings-label">Emoji Usage</div>
          <div className="b2b-settings-label-desc">How many emojis in responses?</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'none', label: 'None' },
            { id: 'minimal', label: 'Minimal 😊' },
            { id: 'moderate', label: 'Moderate 🎉✨' }
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleChange('emoji_usage', opt.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: aiSettings.emoji_usage === opt.id 
                  ? '2px solid #d4a853' 
                  : '1px solid #e5e7eb',
                background: aiSettings.emoji_usage === opt.id ? '#fef9e7' : 'white',
                cursor: 'pointer',
                fontWeight: aiSettings.emoji_usage === opt.id ? 600 : 400
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lead Capture Level */}
      <div className="b2b-settings-row">
        <div>
          <div className="b2b-settings-label">Lead Capture Approach</div>
          <div className="b2b-settings-label-desc">How aggressively to ask for contact info</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'soft', label: '🌿 Soft', desc: 'After 6+ messages' },
            { id: 'balanced', label: '⚖️ Balanced', desc: 'After 3-4 messages' },
            { id: 'aggressive', label: '🔥 Aggressive', desc: 'Ask early & often' }
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleChange('lead_capture_level', opt.id)}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: aiSettings.lead_capture_level === opt.id 
                  ? '2px solid #d4a853' 
                  : '1px solid #e5e7eb',
                background: aiSettings.lead_capture_level === opt.id ? '#fef9e7' : 'white',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>{opt.label}</div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Expertise Focus */}
      <div style={{ marginTop: '32px', marginBottom: '32px' }}>
        <div className="b2b-settings-label" style={{ marginBottom: '8px' }}>Expertise Focus</div>
        <div className="b2b-settings-label-desc" style={{ marginBottom: '16px' }}>
          What should the AI emphasize? Select all that apply.
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {EXPERTISE_OPTIONS.map(opt => {
            const isSelected = (aiSettings.expertise_focus || []).includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleExpertise(opt.id)}
                style={{
                  padding: '10px 16px',
                  borderRadius: '20px',
                  border: isSelected ? '2px solid #10b981' : '1px solid #e5e7eb',
                  background: isSelected ? '#ecfdf5' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
              >
                <span>{opt.emoji}</span>
                <span style={{ fontWeight: isSelected ? 600 : 400 }}>{opt.label}</span>
                {isSelected && <span style={{ color: '#10b981' }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Instructions */}
      <div style={{ marginBottom: '32px' }}>
        <div className="b2b-settings-label" style={{ marginBottom: '8px' }}>Custom Instructions</div>
        <div className="b2b-settings-label-desc" style={{ marginBottom: '12px' }}>
          Add specific guidelines for your AI (one per line)
        </div>
        <textarea
          className="b2b-form-textarea"
          placeholder={`Examples:
• Always mention our price-match guarantee
• Recommend our partner hotels in Cancun first
• We specialize in destination weddings
• Mention we offer 24/7 support`}
          style={{ 
            minHeight: '120px', 
            width: '100%',
            fontFamily: 'inherit',
            lineHeight: 1.6
          }}
          value={aiSettings.custom_instructions || ''}
          onChange={(e) => handleChange('custom_instructions', e.target.value)}
        />
      </div>

      {/* FAQ Auto-Responses */}
      <div style={{ marginBottom: '32px' }}>
        <div className="b2b-settings-label" style={{ marginBottom: '8px' }}>Quick Responses (FAQ)</div>
        <div className="b2b-settings-label-desc" style={{ marginBottom: '16px' }}>
          Define automatic responses for common questions
        </div>

        {/* Existing FAQs */}
        {(aiSettings.faq_responses || []).length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            {aiSettings.faq_responses.map((faq, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '8px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                    When user asks about:
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>"{faq.trigger}"</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                    Respond with:
                  </div>
                  <div style={{ fontSize: '14px' }}>{faq.response}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFaq(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '18px'
                  }}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add New FAQ */}
        <div style={{ 
          padding: '16px', 
          background: '#f9fafb', 
          borderRadius: '12px',
          border: '1px dashed #d1d5db'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
              Trigger phrase (what user might ask)
            </label>
            <input
              type="text"
              className="b2b-form-input"
              placeholder="e.g., hours, payment, cancellation policy"
              style={{ marginTop: '6px', width: '100%' }}
              value={newFaq.trigger}
              onChange={(e) => setNewFaq(prev => ({ ...prev, trigger: e.target.value }))}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
              Auto-response
            </label>
            <textarea
              className="b2b-form-textarea"
              placeholder="e.g., We're available Mon-Fri 9am-6pm EST. You can also email us anytime!"
              style={{ marginTop: '6px', minHeight: '80px', width: '100%' }}
              value={newFaq.response}
              onChange={(e) => setNewFaq(prev => ({ ...prev, response: e.target.value }))}
            />
          </div>
          <button
            type="button"
            onClick={addFaq}
            disabled={!newFaq.trigger.trim() || !newFaq.response.trim()}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: newFaq.trigger && newFaq.response ? '#10b981' : '#e5e7eb',
              color: newFaq.trigger && newFaq.response ? 'white' : '#9ca3af',
              cursor: newFaq.trigger && newFaq.response ? 'pointer' : 'not-allowed',
              fontWeight: 500
            }}
          >
            + Add Response
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Success Message */}
      {saveSuccess && (
        <div style={{
          padding: '12px 16px',
          background: '#ecfdf5',
          border: '1px solid #10b981',
          borderRadius: '8px',
          color: '#065f46',
          marginBottom: '16px'
        }}>
          ✅ AI settings saved successfully!
        </div>
      )}

      {/* Save Button */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button 
          className="b2b-btn b2b-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : '💾 Save AI Settings'}
        </button>
      </div>
    </div>
  );
}
